/**
 * Derivacao do quadrante da matriz de Eisenhower (spec 4.1).
 *
 * Fica em `shared` — e nao em `apps/api/services` como sugere a spec 12 —
 * porque o front precisa da MESMA regra para a escrita otimista: se o cliente
 * usasse uma copia da formula, uma divergencia de uma linha faria o card pular
 * de quadrante ao chegar a resposta do servidor. Funcao pura, sem I/O.
 */

import {
  IMPORTANT_PRIORITY_THRESHOLD,
  QUADRANT,
  URGENT_WINDOW_MS,
  type Priority,
  type Quadrant,
} from './constants.js';

export interface QuadrantInput {
  priority: Priority;
  dueAt: number | null;
}

/**
 * urgente := due_at != null AND due_at <= agora + 48h
 *
 * Tarefa vencida satisfaz a condicao (due_at no passado tambem e <= agora+48h),
 * que e o comportamento desejado: vencida e o caso mais urgente que existe.
 */
export function isUrgent(dueAt: number | null, now: number): boolean {
  if (dueAt === null) return false;
  return dueAt <= now + URGENT_WINDOW_MS;
}

/** importante := priority >= 2 */
export function isImportant(priority: Priority): boolean {
  return priority >= IMPORTANT_PRIORITY_THRESHOLD;
}

export function deriveQuadrant(task: QuadrantInput, now: number): Quadrant {
  const urgent = isUrgent(task.dueAt, now);
  const important = isImportant(task.priority);

  if (urgent && important) return QUADRANT.URGENT_IMPORTANT;
  if (!urgent && important) return QUADRANT.NOT_URGENT_IMPORTANT;
  if (urgent && !important) return QUADRANT.URGENT_NOT_IMPORTANT;
  return QUADRANT.NEITHER;
}

/**
 * Quadrante efetivo de uma tarefa, respeitando o override manual.
 *
 * - `quadrantAuto = true`  -> recalcula pela regra.
 * - `quadrantAuto = false` -> honra o valor fixado pelo usuario (drag & drop).
 *   Se por algum motivo o valor fixado estiver nulo, cai de volta na regra em
 *   vez de devolver null: um card sem quadrante sumiria da matriz.
 */
export function effectiveQuadrant(
  task: QuadrantInput & { quadrant: Quadrant | null; quadrantAuto: boolean },
  now: number,
): Quadrant {
  if (!task.quadrantAuto && task.quadrant !== null) return task.quadrant;
  return deriveQuadrant(task, now);
}

/**
 * Opacidade da barra de urgencia — o elemento-assinatura da interface (spec 7.3).
 *
 * 1.0 quando vencida, decaindo linearmente ate 0.3 em 7 dias ou mais.
 * Tarefa sem prazo fica no piso: ela nao tem urgencia a comunicar.
 */
const URGENCY_FLOOR = 0.3;
const URGENCY_HORIZON_MS = 7 * 24 * 60 * 60 * 1000;

export function urgencyOpacity(dueAt: number | null, now: number): number {
  if (dueAt === null) return URGENCY_FLOOR;

  const remaining = dueAt - now;
  if (remaining <= 0) return 1;
  if (remaining >= URGENCY_HORIZON_MS) return URGENCY_FLOOR;

  const proximity = 1 - remaining / URGENCY_HORIZON_MS;
  return Number((URGENCY_FLOOR + (1 - URGENCY_FLOOR) * proximity).toFixed(3));
}
